import { Button, ButtonProps, Empty, Row, Select, SelectProps, Skeleton, Typography, theme } from 'antd';
import dynamic from 'next/dynamic';
import { useMemo, useRef, useState } from 'react';
import { Omit } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { GrispiInputProps } from '..';
import useAutocompleteUserSearch, {
    AutoCompleteUserKeyMap
} from '../../../hooks/autocomplete/useAutocompleteUserSearch';
import useAutocompleteUserSuggestions from '../../../hooks/autocomplete/useAutocompleteUserSuggestions';
import useDebounce from '../../../hooks/useDebounce';
import useToggle from '../../../hooks/useToggle';
import CustomDivider from '../../CustomDivider';
import { CustomFieldOptionType } from '../../CustomField/CustomField.type';
import LoadingMenu from '../../LoadingMenu';
import { allBuiltinUserValuesForActions } from '../../TriggerPage/NewTriggerForm';
import GrispiInputContainer from '../GrispiInputContainer';
import { isUserValueFormatNeeded, useConservativeValue } from './GrispiMultiUserInput';

type GrispiUserInputProps = Omit<GrispiInputProps<'USER'>, 'type'>;
const DynamicUserModal = dynamic(() => import('../../../components/CreateRequesterModal'));

const initialSearchTerm = '';

const parseOptionsFromValue = (value: string) => ({ label: value.split('|').at(1) ?? value, value });

const hydrateUserOptionsFromValue: (
    value: string,
    defaultOptions: Omit<CustomFieldOptionType, 'order'>[]
) => Omit<CustomFieldOptionType, 'order'>[] = (value, defaultOptions = []) => {
    if (typeof value === 'string') {
        if (allBuiltinUserValuesForActions.includes(value)) return defaultOptions;
        return [...defaultOptions, parseOptionsFromValue(value)];
    }
    return defaultOptions;
};

const transformUserValue = (value: string) => {
    if (value && typeof value === 'string') return value;
    return null;
};

const transformFormValue = (value: string) => {
    if (value && typeof value === 'string') return value;
};

const GrispiUserInput = ({
    antdProps = {},
    conditionInput,
    containerProps,
    isNewValueAllowed,
    isAgentInput,
    onBlur,
    onChange,
    options,
    value,
    ...rest
}: GrispiUserInputProps) => {
    const { ref } = antdProps;
    const { t } = useTranslation('forms');
    const {
        token: { marginXXS, colorError, colorTextLightSolid }
    } = theme.useToken();
    const [conservativeValue, setConservativeValue] = useState<null | string>(null);
    const addButtonRef = useRef<HTMLSelectElement>();
    const selectRef = useRef<HTMLSelectElement>(ref);
    const [isUserModalOpen, , openUserModal, closeUserModal] = useToggle(false);
    const [userModalValue, setUserModalValue] = useState('');
    const autocompleteKey = isAgentInput ? AutoCompleteUserKeyMap.agent : AutoCompleteUserKeyMap.user;
    const [searchTerm, setSearchTerm] = useState<string>(initialSearchTerm);
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const { data: searchResult, isLoading, error } = useAutocompleteUserSearch(debouncedSearchTerm, autocompleteKey);
    const { data: suggestions, setIsSuggestionRequested } = useAutocompleteUserSuggestions(
        autocompleteKey,
        !searchResult
    );
    const suggestionsWithoutSingleItem = suggestions?.filter(
        (item) => item.value?.toString()?.split('|')?.at(0) !== value?.split('|')?.at(0)
    );
    const isValueReadyForRender =
        value && conditionInput
            ? !isUserValueFormatNeeded(value) || (conservativeValue && !isUserValueFormatNeeded(conservativeValue))
            : true;
    useConservativeValue(value, setConservativeValue, conditionInput && !isValueReadyForRender);

    const handleOnClick: ButtonProps['onClick'] = () => {
        onChange(searchTerm);
        selectRef.current?.blur();
        setUserModalValue(searchTerm);
        openUserModal();
    };

    const handleOnChange: SelectProps<string>['onChange'] = (value) => {
        onChange(transformUserValue(value));
        conservativeValue && setConservativeValue(null);
        selectRef.current?.blur();
    };

    const handleOnBlur: SelectProps<string | string[]>['onBlur'] = () => {
        if (onBlur) onBlur();
        setSearchTerm(initialSearchTerm);
    };

    const onEnterKeyDown: SelectProps<string | string[]>['onInputKeyDown'] = ({ key }) => {
        if (key === 'Enter' && addButtonRef.current) addButtonRef.current.click();
    };

    const notFoundContent = isLoading ? (
        <LoadingMenu />
    ) : error ? (
        <Row justify="center">
            <Typography.Text style={{ color: colorError, marginBottom: marginXXS }}>{error.message}</Typography.Text>
        </Row>
    ) : debouncedSearchTerm === initialSearchTerm ? (
        <Row justify="center">
            <Typography.Text style={{ marginBottom: marginXXS }}> {t('autocomplete.empty')}</Typography.Text>
        </Row>
    ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}>
            {isNewValueAllowed ? (
                <Button type="primary" onClick={handleOnClick} ref={addButtonRef} className="full-width">
                    <Typography.Text style={{ color: colorTextLightSolid }} ellipsis>
                        {`${t('autocomplete.add')} "${searchTerm || debouncedSearchTerm}"`}
                    </Typography.Text>
                </Button>
            ) : null}
        </Empty>
    );
    const realOptions = useMemo(() => {
        return isLoading
            ? []
            : searchResult
            ? [...searchResult]
            : suggestionsWithoutSingleItem
            ? [...hydrateUserOptionsFromValue(conservativeValue ?? value, options), ...suggestionsWithoutSingleItem]
            : hydrateUserOptionsFromValue(conservativeValue ?? value, options);
    }, [isLoading, searchResult, suggestionsWithoutSingleItem, conservativeValue, value, options]);

    if (isValueReadyForRender) {
        return (
            <>
                <GrispiInputContainer {...containerProps}>
                    <Select<string>
                        // tokenSeparators={[',', ' ']}
                        allowClear
                        showSearch
                        {...antdProps}
                        loading={isLoading}
                        notFoundContent={notFoundContent}
                        onBlur={handleOnBlur}
                        onChange={handleOnChange}
                        onInputKeyDown={onEnterKeyDown}
                        onSearch={(value) => setSearchTerm(value?.toString())}
                        options={realOptions}
                        filterOption={false}
                        value={conservativeValue ?? transformFormValue(value)}
                        onDropdownVisibleChange={setIsSuggestionRequested}
                        dropdownRender={(option) => (
                            <>
                                {!debouncedSearchTerm && !searchResult?.length && !!realOptions?.length && (
                                    <Row justify="center">
                                        <Typography.Text style={{ marginBottom: marginXXS }}>
                                            {t('autocomplete.empty')}
                                        </Typography.Text>
                                        <CustomDivider zeroMargin />
                                    </Row>
                                )}
                                {debouncedSearchTerm && !searchResult?.length && !isLoading && !error && (
                                    <Row justify="center">
                                        <Typography.Text style={{ marginBottom: marginXXS }}>
                                            {t('autocomplete.noResults')}
                                        </Typography.Text>
                                        <CustomDivider zeroMargin />
                                    </Row>
                                )}
                                {error && !!realOptions?.length && (
                                    <Row justify="center">
                                        <Typography.Text style={{ color: colorError, marginBottom: marginXXS }}>
                                            {error.message}
                                        </Typography.Text>
                                        <CustomDivider zeroMargin />
                                    </Row>
                                )}
                                {option}
                            </>
                        )}
                        ref={selectRef as any}
                        {...rest}
                    />
                </GrispiInputContainer>
                {isNewValueAllowed && isUserModalOpen && (
                    <DynamicUserModal
                        isOpen={isUserModalOpen}
                        closeUserModal={closeUserModal}
                        value={userModalValue}
                        setUserValue={(value: string) => {
                            onChange(transformUserValue(value));
                            selectRef.current?.blur();
                        }}
                    />
                )}
            </>
        );
    }
    return (
        <GrispiInputContainer {...containerProps}>
            <Skeleton.Input active className="full-width" />
        </GrispiInputContainer>
    );
};

export default GrispiUserInput;
